import type { Variant } from '@src/types';

const ALL_VALUE = '__all__';
const BASE_LENGTH = 2; // partnerCode_parentSku

/** A filterable dimension derived from variant data (size, season, etc.). */
export interface FilterDimension {
  key: string;
  label: string;
  /** Parallel array to variant.skus — value at index i is for skus[i]. */
  allValues: string[];
}

/**
 * Build scope keys from variant/filter state. Determines what images are displayed
 * and where saves go. Only partnerCode_parentSku (first 2 segments) are reliably
 * ordered; color position is detected dynamically from the SKU structure.
 */
export function buildScopeKeys(
  selectedColorway: string,
  selectedVariant: Variant | null,
  selectedFilters: Record<string, string>,
  filterDimensions: FilterDimension[],
): string[] {
  if (
    !selectedColorway ||
    !selectedVariant ||
    selectedVariant.skus.length === 0
  )
    return [];

  const firstSku = selectedVariant.skus[0];
  const skuParts = firstSku.split('_');

  if (skuParts.length <= BASE_LENGTH) {
    return [selectedColorway];
  }

  // Find color position in the variable segments (everything after the base).
  // Case-insensitive: colorCode may be lowercase while SKU segments are uppercase.
  const variableParts = skuParts.slice(BASE_LENGTH);
  const colorCodeLower = selectedVariant.colorCode.toLowerCase();
  let colorIdx = variableParts.findIndex(
    part => part.toLowerCase() === colorCodeLower,
  );

  // Fallback: if colorCode not found by exact match, compare across all variant SKUs
  // to find the position that stays constant (which is the color)
  if (colorIdx === -1 && selectedVariant.skus.length > 1) {
    const parsedSkus = selectedVariant.skus.map(s => s.split('_'));
    for (let vi = 0; vi < variableParts.length; vi++) {
      const allSame = parsedSkus.every(
        parts => parts[BASE_LENGTH + vi] === variableParts[vi],
      );
      if (allSame) {
        colorIdx = vi;
        break;
      }
    }
  }

  // Build a map: non-color variable positions (in order) → filterDimensions (in order)
  const positionToDimIdx = new Map<number, number>();
  let dimCounter = 0;
  for (let vi = 0; vi < variableParts.length; vi++) {
    if (vi === colorIdx) continue;
    if (dimCounter < filterDimensions.length) {
      positionToDimIdx.set(vi, dimCounter);
      dimCounter++;
    }
  }

  // Build the scope key segment by segment
  const keyParts = [...skuParts.slice(0, BASE_LENGTH)];

  for (let vi = 0; vi < variableParts.length; vi++) {
    if (vi === colorIdx) {
      keyParts.push(variableParts[vi]);
      continue;
    }

    const dimIdx = positionToDimIdx.get(vi);
    if (dimIdx === undefined) {
      keyParts.push('???');
      continue;
    }

    const dim = filterDimensions[dimIdx];
    const filterValue = selectedFilters[dim.key];

    if (filterValue && filterValue !== ALL_VALUE) {
      // Find a SKU with this display value to extract the code at this position
      const skuIndex = dim.allValues.findIndex(val => val === filterValue);
      if (skuIndex !== -1 && selectedVariant.skus[skuIndex]) {
        const matchParts = selectedVariant.skus[skuIndex].split('_');
        keyParts.push(matchParts[BASE_LENGTH + vi]);
      } else {
        keyParts.push(filterValue);
      }
    } else {
      keyParts.push('???');
    }
  }

  return [keyParts.join('_')];
}

/**
 * Build scope keys for reading — determines what images to display.
 * Uses pattern matching: for each non-wildcard position in the scope key pattern,
 * the candidate key must have the same value or ???.
 */
export function buildScopeKeysForReading(
  scopeKeys: string[],
  matchingSkus: string[],
  selectedVariant: Variant | null,
  imagesByColorway: Record<string, Record<string, string>> | undefined,
  studioImagesByColorway: Record<string, Record<string, string>> | undefined,
  videosByColorway?: Record<string, Record<string, string>>,
): string[] {
  if (!selectedVariant || selectedVariant.skus.length === 0) return scopeKeys;

  const keys = new Set<string>(scopeKeys);

  if (matchingSkus.length > 0) {
    matchingSkus.forEach(sku => keys.add(sku));
  } else {
    const patternParts = scopeKeys[0]?.split('_');
    if (patternParts) {
      const existingPartnerKeys = Object.keys(imagesByColorway || {});
      const existingStudioKeys = Object.keys(studioImagesByColorway || {});
      const existingVideoKeys = Object.keys(videosByColorway || {});

      for (const key of [
        ...existingPartnerKeys,
        ...existingStudioKeys,
        ...existingVideoKeys,
      ]) {
        const keyParts = key.split('_');
        if (keyParts.length !== patternParts.length) continue;

        let matches = true;
        for (let i = 0; i < patternParts.length; i++) {
          if (patternParts[i] === '???') continue;
          if (keyParts[i] === '???') continue;
          if (patternParts[i] !== keyParts[i]) {
            matches = false;
            break;
          }
        }
        if (matches) keys.add(key);
      }
    }
  }

  return Array.from(keys);
}
